import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import {
  Encoding,
  LineMarkOptions,
  MarkInterpolation,
  MarkNullTreatment,
  MarkTransform,
  MarkType,
} from '@mode-vegas/vegas';
import { IconComponent, trackByFactory } from '@mode/capra';
import { resolveLineTransform } from '@mode/charts/util-muze';
import { TransformItem, getMarks } from './layer-options-menu-helpers';

@Component({
  standalone: true,
  imports: [CommonModule, IconComponent, MatDividerModule, MatLegacyMenuModule],
  selector: 'mode-line-options-menu',
  templateUrl: './line-options-menu.component.html',
  styleUrls: ['./line-options-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineOptionsMenuComponent implements OnChanges {
  @Input() markLayerOptions: LineMarkOptions | undefined;
  @Input() encoding!: Encoding;
  @Input() seriesField: string | undefined;

  @Output() layerOptionsChanged = new EventEmitter<LineMarkOptions>();

  trackByValue = trackByFactory<TransformItem>(({ transform }) => transform);

  transformItems: TransformItem[] = [];
  transform: MarkTransform | undefined;

  interpolationItems: Array<{ label: string; type: MarkInterpolation }>;

  isBreakOnNulls = false;
  isConnectDashed = false;
  lineConnectionType: MarkInterpolation = MarkInterpolation.Linear;

  constructor() {
    this.transformItems = [
      { label: 'Overlapped', transform: MarkTransform.Group },
      { label: 'Stacked', transform: MarkTransform.Stack },
    ];
    this.interpolationItems = [
      { label: 'Linear', type: MarkInterpolation.Linear },
      { label: 'Spline', type: MarkInterpolation.Spline },
      { label: 'Step', type: MarkInterpolation.Step },
      { label: 'Step before', type: MarkInterpolation.StepBefore },
      { label: 'Step after', type: MarkInterpolation.StepAfter },
    ];
  }

  ngOnChanges(): void {
    this.transformItems = this.makeTransformItems();
    this.transform = this.getTransform();
    if (this.markLayerOptions) {
      const nullTreatment = this.markLayerOptions.nullTreatment ?? MarkNullTreatment.Break;
      this.isBreakOnNulls = nullTreatment === MarkNullTreatment.Break;
      this.isConnectDashed = nullTreatment === MarkNullTreatment.ConnectDashed;
      this.lineConnectionType = this.markLayerOptions.interpolation ?? MarkInterpolation.Linear;
    } else {
      this.isBreakOnNulls = false;
      this.isConnectDashed = false;
      this.lineConnectionType = MarkInterpolation.Linear;
    }
  }

  setTransform(transform: MarkTransform): void {
    if (this.transform !== transform) {
      this.layerOptionsChanged.emit({ type: MarkType.Line, transform });
    }
  }

  setBreakOnNulls() {
    if (!this.isBreakOnNulls) {
      this.layerOptionsChanged.emit({ type: MarkType.Line, nullTreatment: MarkNullTreatment.Break });
    }
  }

  setConnectDashed() {
    if (!this.isConnectDashed) {
      this.layerOptionsChanged.emit({ type: MarkType.Line, nullTreatment: MarkNullTreatment.ConnectDashed });
    }
  }

  setInterpolation(type: MarkInterpolation) {
    if (this.lineConnectionType !== type) {
      this.layerOptionsChanged.emit({ type: MarkType.Line, interpolation: type });
    }
  }

  private makeTransformItems() {
    const allMarks = getMarks(this.encoding, this.seriesField);
    if (allMarks.length === 0) {
      return [];
    }

    // We only allow the user to switch between Group (Overlaid) or Stack (Stacked).
    // However, the encoding may not support one or both of those. We can check
    // for support by calling 'resolveLineTransform'. If the passed transform
    // matches the return, then it is supported.
    const possibleTransforms = [MarkTransform.Group, MarkTransform.Stack];
    const resolvedTransforms = possibleTransforms.map((transform) =>
      resolveLineTransform(this.encoding, allMarks[0], transform)
    );

    const items: Array<TransformItem> = [];
    const transformMap = new Map([
      [MarkTransform.Group, 'Overlapped'],
      [MarkTransform.Stack, 'Stacked'],
    ]);

    resolvedTransforms.forEach((transform, index) => {
      if (transform === possibleTransforms[index]) {
        items.push({ label: transformMap.get(transform) ?? '', transform });
      }
    });
    return items;
  }

  private getTransform() {
    const allMarks = getMarks(this.encoding, this.seriesField);
    if (allMarks.length === 0) {
      return MarkTransform.Group;
    }
    return resolveLineTransform(this.encoding, allMarks[0], this.markLayerOptions?.transform);
  }
}
